export type ModuleStatus = 'development' | 'beta' | 'stable' | 'planned';
export type NavigationPlacement = 'primary' | 'launcher' | 'hidden';
/**
 * Das Zeichen neben einem Navigationseintrag.
 *
 * Vier Arten von Namen:
 *
 * 1. Fuenfzehn einfarbige Namen aus dem FORQ Icon Pack v1. Sie werden als MASKE gezeichnet,
 *    damit sie die Farbe des Zustands annehmen (grau, im aktiven Eintrag rot). Ein festfarbiges
 *    Bild koennte das nicht. Die massgebliche Liste ist `BILD_ICONS` in
 *    `packages/ui/src/shell/AppShell.tsx` -- diese Union ist alphabetisch sortiert und taugt
 *    nicht als Aufzaehlung. Dateien liegen in `packages/ui/src/styles/nav-icons/`, die
 *    Zuordnung Name -> Datei in `packages/ui/src/styles/shell.css`.
 * 2. `forqy` ist der Sonderfall: das Maskottchen traegt rote Augen, roten Mund und den roten
 *    Bogen, und daran haengt sein Charakter. Als Maske waere es einfarbig und das Gesicht weg,
 *    deshalb wird es als richtiges Bild gezeichnet -- und bleibt folglich in jedem Zustand
 *    gleich bunt. Einzige Ausnahme ist der erzwungene Kontrastmodus: dort waere ein zu 71 %
 *    weisses Bild auf hellem Grund unsichtbar, also wird auch FORQY als Maske gezeichnet.
 *    Die massgebliche Liste ist `FARBIGE_ICONS` in derselben Datei.
 * 3. Vier Namen ohne Bild im Pack (`auswertung`, `verkauf`, `dienstleistungen`,
 *    `datenuebernahme`). Sie tragen eine Strichgrafik, die wenigstens ihr Thema nennt; die
 *    Zuordnung steht in `packages/ui/src/shell/AppShell.tsx`.
 * 4. Zwei allgemeine Namen (`admin`, `module`) fuer alles Uebrige.
 *
 * Ein neues Zeichen braucht drei Schritte: Namen hier ergaenzen, Bild samt CSS-Klasse in
 * `shell.css` anlegen, Namen in die passende Menge in AppShell.tsx aufnehmen -- einfarbig in
 * `BILD_ICONS`, mehrfarbig in `FARBIGE_ICONS`. Die Wahl der Menge ist keine Formsache: ein
 * buntes Bild in `BILD_ICONS` wird als Maske gezeichnet und verliert dabei genau die Farben,
 * derentwegen es bunt ist.
 *
 * Die beiden moeglichen Fehlschlaege sehen verschieden aus. Fehlt die CSS-Klasse, bleibt die
 * Stelle leer -- das faellt auf. Fehlt der Eintrag in beiden Mengen, faellt der Name still auf
 * das Dashboard-Zeichen zurueck; der Eintrag traegt dann ein falsches Zeichen statt gar keines,
 * und das faellt niemandem auf. Deshalb prueft `packages/ui/src/styles/navIcons.test.ts` beide
 * Richtungen.
 */
export type NavigationIcon =
  | 'artikel'
  | 'dashboard'
  | 'dienstleistungen'
  | 'dokumente'
  | 'ersatzteile'
  | 'feedback'
  | 'finanzen'
  | 'kunden'
  | 'lager'
  | 'lieferanten'
  | 'maschinen'
  | 'messenger'
  | 'mietpark'
  | 'service'
  | 'studio'
  | 'vorgaenge'
  | 'forqy'
  | 'auswertung'
  | 'verkauf'
  | 'datenuebernahme'
  | 'admin'
  | 'module';
export type UiSlot =
  | 'dashboard.main'
  | 'dashboard.aside'
  | 'admin.main'
  | 'admin.aside'
  | 'header.actions'
  | 'launcher';

export interface NavigationItem {
  readonly id: string;
  readonly label: string;
  readonly route: string;
  readonly placement: NavigationPlacement;
  readonly order: number;
  readonly icon?: NavigationIcon;
}

export interface ForqModuleManifest {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly version: string;
  readonly route: string;
  readonly category: string;
  readonly status: ModuleStatus;
  readonly requiredPermissions: readonly string[];
  readonly features: readonly string[];
  readonly navigation: readonly NavigationItem[];
}

export interface ForqFeatureManifest {
  readonly id: string;
  readonly moduleId: string;
  readonly name: string;
  readonly description: string;
  readonly version: string;
  readonly defaultEnabled: boolean;
  readonly requiredPermissions: readonly string[];
  readonly uiSlots: readonly UiSlot[];
  readonly emits: readonly string[];
  readonly listensTo: readonly string[];
}

export interface ForqEvent<TPayload = unknown> {
  readonly type: string;
  readonly payload: TPayload;
  readonly occurredAt: string;
  readonly source: string;
}

/**
 * Nummernpräfix eines Mandanten: eine Ziffer 1–9.
 *
 * Hier stand bis 2026-09-08 der Literaltyp `'1' | '2' | '3'` — die drei Firmen,
 * für die FORQ ursprünglich entworfen wurde. Der Supabase-Adapter warf beim
 * Start eine Ausnahme, sobald die Datenbank etwas anderes lieferte: ein vierter
 * Kunde brachte die Anwendung zum Absturz, obwohl die Tabelle 1–9 zulässt.
 *
 * Die Datenbank ist die Autorität: `check (number_prefix ~ '^[1-9]$')`, dazu
 * `unique`. Der Typ bildet jetzt genau das ab.
 *
 * Verbleibende harte Grenze, bewusst dokumentiert: **neun Mandanten je
 * Datenbank**. Für ein Produkt mit einer Installation je Kunde ist das reichlich
 * — dort gibt es in aller Regel einen Mandanten. Wer mehr braucht, ändert die
 * Prüfregel in der Datenbank und dieses Muster gemeinsam.
 */
export type TenantNumberPrefix = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

export const TENANT_NUMBER_PREFIXES: readonly TenantNumberPrefix[] =
  ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function isTenantNumberPrefix(value: string): value is TenantNumberPrefix {
  return (TENANT_NUMBER_PREFIXES as readonly string[]).includes(value);
}

export interface TenantSummary {
  readonly id: string;
  readonly name: string;
  readonly shortName: string;
  readonly numberPrefix: TenantNumberPrefix;
}

export interface UserSummary {
  readonly id: string;
  readonly displayName: string;
  readonly role: string;
}

export type PermissionEffect = 'allow' | 'deny';
export type AuditChange = Readonly<Record<string, unknown>>;

export interface TenantMembership {
  readonly userId: string;
  readonly tenantId: string;
  readonly roleIds: readonly string[];
  readonly active: boolean;
}

export interface RolePermissionGrant {
  readonly roleId: string;
  readonly permission: string;
}

export interface UserPermissionOverride {
  readonly userId: string;
  readonly tenantId: string;
  readonly permission: string;
  readonly effect: PermissionEffect;
}

export interface NumberSequenceDefinition {
  readonly id: string;
  readonly tenantId: string;
  readonly key: string;
  readonly year: number | null;
  readonly prefix: string;
  readonly sequenceLength: number;
  readonly nextValue: number;
  readonly requiredPermission: string;
  readonly active: boolean;
}

export interface ClaimedNumber {
  readonly sequenceId: string;
  readonly number: string;
  readonly counterValue: number;
  readonly claimedAt: string;
}

export interface AuditRecordInput {
  readonly tenantId: string | null;
  readonly actorUserId: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId?: string;
  readonly before?: AuditChange;
  readonly after?: AuditChange;
  readonly metadata?: AuditChange;
}

export interface AuditRecord extends AuditRecordInput {
  readonly id: string;
  readonly occurredAt: string;
}

export type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface TaskSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly title: string;
  readonly status: TaskStatus;
  readonly priority: TaskPriority;
  readonly dueAt: string | null;
  readonly sourceModule: string | null;
  readonly sourceEntityType: string | null;
  readonly sourceEntityId: string | null;
}

export interface NotificationSummary {
  readonly id: string;
  readonly tenantId: string | null;
  readonly userId: string;
  readonly type: string;
  readonly title: string;
  readonly body: string | null;
  readonly route: string | null;
  readonly readAt: string | null;
  readonly createdAt: string;
}

/**
 * Die Datenarten, die sich löschen lassen. Absichtlich eine geschlossene Liste: der Wert geht an
 * die Datenbank und entscheidet dort, welche Tabelle angefasst wird.
 */
export type DeletableEntity = 'customer' | 'machine' | 'article' | 'supplier' | 'offer' | 'order' | 'warehouse-location';

/**
 * Was beim Löschen passieren WIRD -- die Grundlage des Bestätigungsdialogs.
 *
 * - 'delete':  nichts hängt dran, der Datensatz verschwindet wirklich.
 * - 'archive': es hängt etwas dran (Rechnungen, Vorgänge, Historie). Der Datensatz wird
 *              ausgeblendet, die Verweise bleiben unangetastet.
 * - 'blocked': darf gar nicht weg (abgerechneter Vorgang -- Aufbewahrungspflicht).
 */
export type DeleteVerdict = 'delete' | 'archive' | 'blocked';

export interface DeleteReference {
  /** Technischer Tabellenname -- für Meldungen und zum Nachschlagen. */
  readonly table: string;
  /** Was der Anwender liest: "Vorgänge", "Serviceaufträge". */
  readonly label: string;
  readonly count: number;
}

export interface DeletePlan {
  readonly entity: DeletableEntity;
  readonly id: string;
  /** Name des Datensatzes, damit im Dialog steht, was gemeint ist. */
  readonly label: string;
  readonly archived: boolean;
  readonly verdict: DeleteVerdict;
  readonly references: readonly DeleteReference[];
  readonly referenceCount: number;
  /** Nur bei 'blocked' gesetzt: warum es nicht geht. */
  readonly blockedReason: string | null;
}

export interface DeleteOutcome {
  readonly action: 'deleted' | 'archived';
  readonly label: string;
  readonly references?: readonly DeleteReference[];
  /**
   * true, wenn archiviert wurde, obwohl die Vorschau "löschen" angekündigt hatte -- die Datenbank
   * hat einen Verweis gefunden, den die Vorschau übersah. Die Oberfläche sagt das ausdrücklich:
   * ein stiller Unterschied zwischen Ankündigung und Ergebnis wäre schlimmer als der Fehler.
   */
  readonly unexpected?: boolean;
}

/**
 * Löschen quer über die Module. Bewusst EIN Port statt einer Methode je Modul: die Frage
 * "hängt da noch was dran?" ist überall dieselbe, und sie falsch zu beantworten kostet Daten.
 */
export interface RecordDeletionGateway {
  /** Was würde passieren? Wird VOR dem Bestätigen abgefragt. */
  planDeletion(tenantId: string, entity: DeletableEntity, id: string): Promise<DeletePlan>;
  deleteRecord(tenantId: string, entity: DeletableEntity, id: string): Promise<DeleteOutcome>;
  restoreRecord(tenantId: string, entity: DeletableEntity, id: string): Promise<void>;
}

export type AnnouncementTone = 'info' | 'warning' | 'critical';

/**
 * Wie oft die Systemmeldung beim Start erscheint.
 * - 'every-start': stehende Warnung, kommt jedes Mal wieder ("Achtung Beta-Phase!").
 * - 'once':        einmalige Nachricht ("Changelog V1.1"), bis sie quittiert wird.
 *
 * Der Unterschied ist der Punkt: wer dieselbe Meldung zum vierten Mal sieht, klickt sie weg, ohne
 * zu lesen. Wenn ALLES jedes Mal kommt, geht die eine wichtige Meldung im Reflex unter.
 */
export type AnnouncementRepeat = 'every-start' | 'once';

/** Die aktive Systemmeldung eines Mandanten, so wie ein Benutzer sie beim Start zu sehen bekommt. */
export interface SystemAnnouncement {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly tone: AnnouncementTone;
  readonly repeat: AnnouncementRepeat;
  /** Aendert der Admin den Text, gilt eine 'once'-Meldung wieder als ungelesen. */
  readonly updatedAt: string;
}

export interface Credentials {
  readonly email: string;
  readonly password: string;
}

export interface AuthenticatedSession {
  readonly userId: string;
  readonly email: string | null;
  readonly displayName: string | null;
}

export type SessionListener = (session: AuthenticatedSession | null) => void;

export interface AuthGateway {
  getSession(): Promise<AuthenticatedSession | null>;
  signIn(credentials: Credentials): Promise<AuthenticatedSession>;
  signOut(): Promise<void>;
  onSessionChange(listener: SessionListener): () => void;
  /**
   * Schickt eine Mail mit einem Rücksetz-Link. Antwortet BEWUSST auch dann erfolgreich, wenn es die
   * Adresse nicht gibt: sonst wird das Formular zum Werkzeug, um gültige Konten zu finden.
   */
  requestPasswordReset(email: string, redirectTo: string): Promise<void>;
  /** Setzt das Passwort der laufenden Sitzung -- nach dem Rücksetz-Link oder aus dem Profil. */
  updatePassword(password: string): Promise<void>;
  /**
   * Anfrage auf einen Zugang, ohne Konto. Landet im Adminpanel.
   * Der Server prüft die E-Mail-Domain -- das Formular ist öffentlich erreichbar.
   */
  requestAccess(input: AccessRequestInput): Promise<void>;
}

export interface AccessRequestInput {
  readonly email: string;
  readonly displayName: string;
  readonly note: string;
}

// Belegarchiv -- der ausgestellte Beleg, so wie er ausgestellt wurde (§ 14b UStG / GoBD).
export * from './documentArchive';
